import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("customerToken"));
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const checkAuth = useCallback(async () => {
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, { headers: getHeaders() });
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem("customerToken");
      setToken(null);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, getHeaders]);

  const register = async (name, email, phone, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { name, email, phone, password });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem("customerToken", newToken);
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(" ") : "Registration failed";
      return { success: false, error: msg };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem("customerToken", newToken);
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid email or password";
      return { success: false, error: msg };
    }
  };

  const logout = () => {
    localStorage.removeItem("customerToken");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{
      user, token, isAuthenticated, loading,
      register, login, logout, getHeaders, API,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
